import { FC } from 'react';
import { Progress, Typography, Col, Row } from 'antd';
import styled from 'styled-components';
import { StatusField } from '@/entities/proc';
import { ProcProcessingState } from '@/entities/proc/proc-generation.types';
import { themeConfig, useThemeContext } from '@/shared/theme';

export const GeneratingProcContent: FC<ProcProcessingState> = ({
  isError = false,
  prompt,
  progress = { value: 0, message: 'Generating' },
}) => {
  const { theme } = useThemeContext();

  const getProgress = () => {
    if (isError) {
      return {
        value: 0,
        label: 'Error',
      };
    }
    return {
      value: progress?.value ?? 0,
      label: progress?.message ?? 'Starting...',
    };
  };

  const { value, label } = getProgress();

  return (
    <Wrapper
      $backgroundColor={themeConfig[theme].colors.contentBg}
      $borderColor={themeConfig[theme].colors.border}
      $borderHoverColor={themeConfig[theme].colors.borderHover}
      style={{
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Header>
        <Actions>
          <StatusField
            status={isError ? 'error' : 'generating'}
            label={isError ? undefined : label}
          />
        </Actions>
      </Header>
      <Content>
        <PromptText>
          <strong>Prompt:</strong> {prompt}
        </PromptText>
        <ProgressWrapper>
          <Progress percent={value} showInfo={true} size="small" />
        </ProgressWrapper>
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
  $borderHoverColor: string;
}>`
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  transition: all 0.25s ease-in-out;
  background-color: ${({ $backgroundColor }) => $backgroundColor} !important;
  border-color: ${({ $borderColor }) => $borderColor} !important;
  height: 100%;
  min-height: 230px;
  display: flex;
  flex-direction: column;
  &:hover {
    border-color: ${({ $borderHoverColor }) => $borderHoverColor} !important;
  }
`;

const Header = styled.div`
  padding: 8px;
  padding-bottom: 0;
  color: rgba(0, 0, 0, 0.88);
  width: 100%;
  font-size: 12px;
`;

const Actions = styled(Row)`
  justify-content: space-between;
  margin-bottom: 5px;
  padding-left: 8px;
`;

const Content = styled(Col)`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const PromptText = styled(Typography.Text)`
  display: block;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.88);
  white-space: pre-wrap;
  word-break: break-word;
  height: 140px;
  overflow-y: auto;
  padding: 0 8px;
`;

const ProgressWrapper = styled.div`
  padding: 12px 8px;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .ant-progress {
    margin: 0;
    width: 100%;
  }
`;
